<template>
  <div v-if="eventData" class="my-5 m-auto px-0">
    <div class="max-w-screen-xl m-auto px-4">
      <Heading
        :level="1"
        h-class="text-h5 lg:text-h4"
        :title="eventGetters.getTitle(eventData)"
        class="kft-heading--left text-stone-800"
        data-test-id="event-title"
      />
      <div
        class="text-thin text-base my-2 md:line-clamp-2 md:hover:line-clamp-none"
        v-html="eventGetters.getSubtitle(eventData)"
      ></div>
      <div class="flex justify-between items-center mt-4">
        <EventMetaInfo
          :supplier-name="eventGetters.getSupplierName(eventData)"
          :reviews-average="reviewsAverageToDisplay"
          :reviews-type="reviewsTypeToDisplay"
          :reviews-total="reviewsTotalToDisplay"
          :has-review-section="shouldShowReviews"
        />
        <EventShareWishlistButtons
          :event="eventData"
          :is-giftcard="isGiftcard"
        />
      </div>
      <EventGallery
        class="mt-5"
        :event="eventData || null"
        :is-giftcard="isGiftcard"
      />

      <!-- BEGIN::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->
      <div
        ref="eventSidebarContainer"
        :class="{
          'lg:w-2/3': props.isPreview,
        }"
        class="flex flex-col lg:flex-row-reverse gap-4"
      >
        <EventDetailsSidebar v-if="!props.isPreview" ref="sidebarRef" />
        <div class="w-auto flex-1 flex-col gap-5 overflow-hidden">
          <div class="w-full my-8">
            <Heading
              :level="2"
              :title="getTitleByCategory('about', eventData)"
              h-class="text-h5"
              class="kft-heading--left mt-8 mb-2"
            ></Heading>
            <div class="md:pr-8">
              <LazyVisibleStrapiContentWrapper
                hydrate-on-visible
                :rich-text="eventGetters.getDescription(eventData)"
              />
            </div>
            <RenderOnIntersection
              v-if="
                eventGetters.getAttendanceMode(eventData) !== 'ONLINE' &&
                !isSubdomain
              "
            >
              <EventSimilarEventsIntro :is-giftcard="isGiftcard" />
            </RenderOnIntersection>
            <LazyVisibleEventImportantDetails
              v-if="eventData"
              hydrate-on-visible
              :is-giftcard="isGiftcard"
              :event-data="eventData as EventDescription"
              :hide-title="false"
            />
            <div v-if="eventGetters.getCourseProgress(eventData)" class="my-8">
              <Heading
                v-if="!isGiftcard"
                :level="2"
                :title="getTitleByCategory('progress', eventData)"
                h-class="text-h5 font-medium"
                class="kft-heading--left mt-8 mb-2 text-stone-800"
              />
              <div class="md:pr-8">
                <LazyVisibleStrapiContentWrapper
                  hydrate-on-visible
                  :rich-text="eventGetters.getCourseProgress(eventData)"
                />
              </div>
            </div>
            <div v-if="eventGetters.getCourseTarget(eventData)" class="my-8">
              <Heading
                v-if="!isGiftcard"
                :level="2"
                :title="getTitleByCategory('courseTarget', eventData)"
                h-class="text-h5 font-medium"
                class="kft-heading--left mt-8 mb-2 text-stone-800"
              />
              <div class="md:pr-8">
                <LazyVisibleStrapiContentWrapper
                  hydrate-on-visible
                  :rich-text="eventGetters.getCourseTarget(eventData)"
                />
              </div>
            </div>
            <!-- BEGIN::Private Event Info -->
            <LazyVisiblePrivateEventInfo
              v-if="
                eventGetters.getIsTeamEvent(eventData) &&
                eventGetters.getPrivateEventDescription(eventData) !== ''
              "
              hydrate-on-visible
              :description="eventGetters.getPrivateEventDescription(eventData)"
            />
            <!-- END::Private Event Info -->

            <!-- BEGIN::Customer Media Gallery -->
            <LazyVisibleEventCustomerMediaGallery
              v-if="eventData"
              hydrate-on-visible
              :event="eventData"
              @open-review="handleOpenReviewFromGallery"
            />
            <!-- END::Customer Media Gallery -->
          </div>

          <div v-if="addOns && addOns.length > 0" class="mt-10">
            <LazyVisibleEventAddOnsDetails
              hydrate-on-visible
              :add-ons="addOns"
              :add-directly-to-cart="false"
              @click:addon="handleAddOnClick"
            />
          </div>
          <!-- END::Ticket suggestion cards -->
        </div>
      </div>
      <!-- END::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->

      <ErrorBoundary>
        <RenderOnIntersection v-if="!isGiftcard">
          <LazyLocationAndMap :kft-event="eventData || null" />
        </RenderOnIntersection>
      </ErrorBoundary>

      <div
        v-if="!isGiftcard"
        id="sectionPartner"
        ref="partnerSection"
        class="mt-10 w-full bg-stone-50 p-4 rounded-lg"
      >
        <NuxtLink
          :to="localePath(`/p/${eventGetters.getSupplierPermalink(eventData)}`)"
        >
          <LazyVisibleUserProfileHeader
            hydrate-on-visible
            :title-level="2"
            title-h-class="text-h5"
            :subtitle="eventGetters.getSupplierUserName(eventData)"
            :name="eventGetters.getSupplierName(eventData)"
            :image-url="eventGetters.getSupplierLogo(eventData)"
          />
        </NuxtLink>

        <div
          class="text-base text-stone-700 line-clamp-3"
          v-html="eventGetters.getSupplierShortDescription(eventData)"
        ></div>
      </div>
    </div>
    <div
      v-if="shouldShowReviews"
      id="sectionReviews"
      class="product-show-cases !max-w-none py-5 md:py-10 bg-[#F9F7F5] mt-10 scroll-mt-[130px] md:scroll-mt-[160px]"
    >
      <LazyVisibleReviewShowcase
        hydrate-on-visible
        :average-rating="reviewsAverageToDisplay"
        :review-count="reviewsTotalToDisplay"
        :supplier-name="supplierNameToDisplay"
        :reviews="reviewsToDisplay"
        :review-type="reviewsTypeToDisplay"
        :supplier-created-at="supplierCreatedAtToDisplay"
        :display-item-title="false"
        :event-permalink="eventPermalink"
        :review-breakdown="eventGetters.getReviewBreakdown(eventData)"
        :open-with-review-id="reviewToOpenFromGallery"
        @clear-open-review="reviewToOpenFromGallery = undefined"
      />
    </div>
    <div
      class="max-w-screen-xl px-4 m-auto overflow-hidden md:overflow-inherit"
    >
      <!-- Begin::Supplier events -->
      <div class="product-show-cases">
        <LazyEventHorizontalProductShowcase
          v-if="parsedSupplierEvents.length > 0 && eventData"
          hydrate-on-visible
          data-test-id="supplier-events"
          :products="parsedSupplierEvents"
          :gtm-item-list-id="GtmItemListId.EventPageSameSupplierClasses"
          :title="
            isGiftcard
              ? $t('product.giftcardEventsFromTheSameSupplier')
              : $t('product.eventsFromTheSameSupplier')
          "
          algolia-insights-label="Supplier Events - Event Page"
          :loader-products-count="parsedSupplierEvents.length || 6"
        />
        <!-- End::Supplier events -->

        <RenderOnIntersection
          v-if="
            eventData &&
            eventGetters.getAttendanceMode(eventData) !== 'ONLINE' &&
            !isSubdomain
          "
        >
          <EventSimilarEvents :is-giftcard="isGiftcard" />
        </RenderOnIntersection>

        <RenderOnIntersection
          v-if="
            eventData &&
            eventGetters.getMainCategorySlug(eventData) &&
            !isGiftcard &&
            !isSubdomain
          "
        >
          <EventSimilarOnlineEvents />
        </RenderOnIntersection>
      </div>
      <!-- End::Similar Online events -->

      <!-- Begin::Similar Box Events -->
      <div v-if="!isSubdomain && !isGiftcard && eventData">
        <LazyVisibleEventSimilarBoxEvents
          hydrate-on-visible
          :category="eventGetters.getMainCategorySlug(eventData)"
        />
      </div>
      <!-- End::Similar Box Events -->

      <ClientOnly>
        <LazyEventTags v-if="!isSubdomain && eventData" :event="eventData" />
      </ClientOnly>

      <LazyVisibleEventBreadcrumbs
        v-if="!isSubdomain && eventData"
        hydrate-on-visible
        :event-detail="eventData as any"
        :is-giftcard="isGiftcard"
      />
    </div>
    <EventStickyBuyBoxButton
      v-if="!props.isPreview"
      :visible="isSidebarVisible"
      @scroll-to-buybox="scrollToSidebar"
    />
  </div>
</template>

<script lang="ts" setup>
import * as qs from 'qs'
import type { EventDate, EventDescription } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import useEventSchema from '~/composables/schemaOrg/useEventSchema'
import useSeoHelper from '~/composables/useSeoHelper'
import useProductSchema from '~/composables/schemaOrg/useProductSchema'
import { sendProductDetailView } from '~/composables/useGtmEvents'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import {
  useClearEventDataOnUnmount,
  useEventRedirects,
} from '~/composables/useEvent'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { useTitleByCategory } from '~/composables/product/useGetTitleByCategory'
import type { CartItem } from '~/composables/types/cart'
import { setLastViewedEvent } from '~/utils/lastViewedEvents'
import { mergeKftEventDescriptionSettingsIncludes } from '~/utils/kftEventDescriptionIncludes'
import { supplierGetters } from '~/utils/supplierGetters'
import { hasAddToCartCategoryQuantities } from '~/utils/parseAddToCartCategoryQuantitiesFromQuery'
import { hasScrolledPastElement } from '~/utils/scrollVisibility'
// Aliased: a project-local useIntersectionObserver (one-shot flag) shadows the
// VueUse auto-import; we need VueUse's reactive, directional observer here.
import { useIntersectionObserver as useViewportIntersection } from '@vueuse/core'

const { getTitleByCategory } = useTitleByCategory()

interface Props {
  eventPermalink?: string
  isGiftcard?: boolean
  isSpecialGiftcard?: boolean
  shouldAddEventSchema?: boolean
  isPreview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  eventPermalink: undefined,
  isGiftcard: false,
  isSpecialGiftcard: false,
  shouldAddEventSchema: false,
  isPreview: false,
})
const isSubdomain = useIsSubdomain()

const { t: $t } = useI18n()
const $localePath = useLocalePath()
const localePath = $localePath
const { query: queryParams } = useRoute()
const { cart, addItem } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState()
const eventSidebarContainer = ref()
const sidebarRef = useTemplateRef<any>('sidebarRef')
// Observe only the buy box / giftcard area (exposed by the sidebar) so the
// sticky bar appears once that scrolls off — ignoring the team-event and
// corporate-gifting cards below it.
const buyBoxContainer = computed<HTMLElement | null>(
  () => (sidebarRef.value as any)?.buyBoxContainer ?? null
)
const hasStickyBuyBoxTarget = computed<boolean>(() =>
  Boolean((sidebarRef.value as any)?.hasStickyBuyBoxTarget)
)
// The bar may only appear once the buy box area has been scrolled PAST — i.e.
// its bottom edge is above the viewport. A plain visibility check isn't enough:
// it also reports "not visible" while the buy box is still below the fold on
// load, which must NOT show the bar.
const isBuyBoxScrolledPast = ref(false)
useViewportIntersection(buyBoxContainer, ([entry]) => {
  if (!entry) return
  isBuyBoxScrolledPast.value = hasScrolledPastElement(entry)
})
// Hide the bar unless there is a buy box / giftcard target that we've scrolled
// below.
const isSidebarVisible = computed<boolean>(
  () => !hasStickyBuyBoxTarget.value || !isBuyBoxScrolledPast.value
)
const scrollToSidebar = () => {
  const el = buyBoxContainer.value as HTMLElement | null
  if (!el) return
  // height of navbar should be handled here so buybox isn't hidden
  // behind navbar. adding some extra pixels to have some gap between
  // navbar and buybox
  const top = el.getBoundingClientRect().top + window.scrollY - 115
  window.scrollTo({ top, behavior: 'smooth' })
}
const reviewToOpenFromGallery = ref<string | undefined>(undefined)

const handleOpenReviewFromGallery = (reviewId: string) => {
  reviewToOpenFromGallery.value = reviewId
  nextTick(() => {
    document.getElementById('sectionReviews')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  })
}
const { data: eventDataResponse, error: eventDataLoadError } =
  await useKftApiFetch<KftApiDefaultResponse<EventDescription>>(
    `v1/store/events/${props.eventPermalink}${
      props.isPreview ? `/preview` : ``
    }?${qs.stringify(
      {
        include: mergeKftEventDescriptionSettingsIncludes([
          'address.locality',
          'supplier',
          'supplier.logo',
          'reviews',
          'activePromotion',
          'pricing',
        ]),
      },
      { arrayFormat: 'comma' }
    )}`,
    {
      transform: (res) => {
        // remove phone, email, website, address from supplier data
        if (res?.data?.supplier?.data) {
          const { data } = res.data.supplier
          ;['phone', 'email', 'website', 'address', 'description'].forEach(
            (key) => delete data[key]
          )
        }
        return res
      },
    }
  )

const eventData = useEventData()
eventData.value = eventDataResponse.value?.data
useClearEventDataOnUnmount()

// handle event redirects
await useEventRedirects(
  eventData?.value,
  eventDataLoadError?.value,
  props.isGiftcard,
  props.eventPermalink
)

provide('eventBuyBoxVisibleElements', {
  calendar: true,
  requestButtons: true,
  giftcard: true,
})

// Only provide eventData if it exists to prevent null/undefined injection issues
if (eventData.value) {
  provide('eventData', eventData.value)
}
provide('isSpecialGiftcardEvent', props.isSpecialGiftcard)

provide('openReviewInSidebar', handleOpenReviewFromGallery)

/**  DATA FETCHING **/
const {
  add_event_to_cart_quantity: quantity,
  add_event_to_cart_date_id: dateId,
  date_type: paramDateType,
} = queryParams

const eventId = eventGetters.getId(eventData?.value)
const supplierId = computed(() => eventGetters.getSupplierId(eventData.value))
const hasEventReviews = computed(() => {
  if (!eventData.value) return false
  return (
    eventGetters.getReviewsListRecentFirst(eventData.value).length > 0 &&
    eventGetters.getReviewsTotal(eventData.value) > 0
  )
})

// Deferred (immediate:false, client-only) — issues no request here, executed on mount.
const { data: addToCardDate, execute: executeFetchAddToCartDate } =
  await useKftApiFetch<KftApiDefaultResponse<EventDate[]>, EventDate>(
    `v1/store/events/${eventId}/dates?search=id:${dateId};${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}&include=children`,
    {
      server: false,
      immediate: false,
      transform: (res) => {
        return res?.data && res.data[0]
      },
    }
  )

// Supplier-events and add-ons only depend on the resolved event, not on each
// other. useKftApiFetch starts its request on call, so kick them off first (in
// flight) and await them together instead of awaiting each inline — otherwise
// they run as a sequential SSR request waterfall.
const partnerEventsAsync =
  eventData.value && supplierId.value
    ? useFetchSupplierEvents(supplierId.value!, undefined, {
        default: () => ref([]),
        params: {
          include: ['supplier'],
        },
      })
    : null

const addOnsAsync = useKftApiFetch(
  `v1/store/events/${eventId}/add-ons?include=media`
)

await Promise.all([partnerEventsAsync, addOnsAsync].filter(Boolean))

const partnerEvents = partnerEventsAsync?.data ?? ref([])
const { data: addOnsResponse } = addOnsAsync
const addOns = computed(() => addOnsResponse.value?.data)

// The event fetch already includes `supplier`, whose data carries the review
// aggregate (avg_review, total_reviews, created_at, google_*). Source the
// partner-review fallback from it instead of a second /supplier/{id} call.
const supplierData = computed(() => eventGetters.getSupplier(eventData.value))

// Check if partner actually has reviews (not just exists)
const hasPartnerReviews = computed(() => {
  if (!supplierData.value) return false
  const totalReviews = supplierGetters.getReviewsTotal(supplierData.value)
  const avgReview = supplierGetters.getReviewsAverage(supplierData.value)
  return totalReviews > 0 && avgReview > 0
})

const hasThirdPartyReviews = computed(() => {
  const partner = supplierData.value
  return (
    partner?.google_avg_review != null &&
    partner?.google_avg_review > 0 &&
    partner?.google_total_reviews != null &&
    partner?.google_total_reviews > 0
  )
})

/** COMPUTED **/
const parsedSupplierEvents = computed(() => {
  if (!partnerEvents.value || !(partnerEvents.value as any)?.data?.length)
    return []
  return transformToAlgoliaEvent(partnerEvents.value as any)
    .filter((e) => e.hashed_id !== eventData.value?.id)
    .slice(0, 10)
})

// Computed properties for reviews (event reviews only; see reviewsToDisplay note)
const reviewsToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsListRecentFirst(eventData.value)
  }
  // Partner review cards are intentionally not shown on the event page
  // (shouldShowReviews is gated on hasEventReviews), so no list is sourced here.
  return []
})

const reviewsTypeToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return 'event'
  }
  if (hasPartnerReviews.value) {
    return 'partner'
  }
  if (hasThirdPartyReviews.value) {
    return 'third-party'
  }
  return 'event'
})

const reviewsAverageToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsAverage(eventData.value) || 0
  }
  if (hasPartnerReviews.value) {
    return String(supplierGetters.getReviewsAverage(supplierData.value))
  }
  // Fallback to Google reviews from supplier data
  if (supplierData.value?.google_avg_review) {
    return String(supplierData.value.google_avg_review)
  }
  return 0
})

const reviewsTotalToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsTotal(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getReviewsTotal(supplierData.value)
  }
  // Fallback to Google reviews from supplier data
  if (supplierData.value?.google_total_reviews) {
    return supplierData.value.google_total_reviews
  }
  return 0
})

const supplierNameToDisplay = computed(() => {
  return eventGetters.getSupplierName(eventData.value) || ''
})

const supplierCreatedAtToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getSupplierCreatedAt(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getCreatedAt(supplierData.value)
  }
  return ''
})

const shouldShowReviews = computed(() => {
  // Only show review section if event has reviews, not partner reviews
  return hasEventReviews.value
})

const shouldTryToFetchAddToCartLinks = computed(
  () =>
    quantity &&
    dateId &&
    eventId &&
    !hasAddToCartCategoryQuantities(queryParams) &&
    !cart.value.items.find(
      (i) => i.id === dateId && i.quantity === Number(quantity)
    )
)

const handleAddToCartLinks = async () => {
  const qtyTickets = Math.min(
    Number(quantity),
    addToCardDate.value?.availableTicketsQuantity || 1
  )

  if (addToCardDate.value && addToCardDate.value?.dateType && eventData.value) {
    const { dateType } = addToCardDate.value
    const dateIsPrivate = dateType === 'PRIVATE' || paramDateType === 'PRIVATE'

    const pricingsResponse = await useFetchPricings(ref(eventData.value.id), {
      dateId: ref(addToCardDate.value.id),
      quantity: ref(qtyTickets),
      isPrivate: ref(dateIsPrivate),
    })

    if (!pricingsResponse) {
      return
    }

    await book(
      parseEventDateToCartItem(
        eventData.value,
        qtyTickets,
        pricingsResponse,
        addToCardDate as Ref<EventDate>,
        ref([]),
        dateIsPrivate
      )
    )
  }
}

const book = async (params: CartItem) => {
  await addItem(params)
  toggleCartSidebar()
}

const { script: eventSchemas } = useEventSchema(
  eventData.value ? [eventData.value] : []
)
const { script: productSchemas } = useProductSchema(
  eventData.value ? [eventData.value] : []
)

const handleAddOnClick = () => {
  if (!eventSidebarContainer?.value) return

  const offsetHeight = eventSidebarContainer.value.offsetTop ?? 0

  window.scrollTo({
    // subtract 130 to account for navbar
    top: offsetHeight - 130,
    behavior: 'smooth',
  })
}

onMounted(async () => {
  try {
    // Add small delay to stabilize DOM
    await nextTick()

    if (shouldTryToFetchAddToCartLinks.value) {
      await executeFetchAddToCartDate()
      handleAddToCartLinks()
    }

    if (eventData.value) {
      sendProductDetailView(eventData.value)
    }

    try {
      if (eventData.value?.id) {
        setLastViewedEvent(eventData.value.id)
      }
    } catch (error) {
      console.warn('Failed to set last viewed event:', error)
    }
  } catch (e) {
    console.error('Error in EventDetails onMounted:', e)
  }
})

const seoOptions = computed(() => ({
  head: {
    script: [
      props.shouldAddEventSchema ? eventSchemas : null,
      props.isGiftcard ? productSchemas : null,
    ].filter(Boolean),
    title: eventData.value ? eventGetters.getTitle(eventData.value) : '',
  },
  shouldBeIndexed: !props.isPreview,

  meta: {
    title:
      (eventData.value ? eventGetters.getTitle(eventData.value) : '') +
      ' | ' +
      $t('general.titles.base'),
    'og:title':
      (eventData.value ? eventGetters.getTitle(eventData.value) : '') +
      ' | ' +
      $t('general.titles.base'),
    description: eventData.value
      ? eventGetters.getMetaDescription(eventData.value)
      : '',
    'og:description': eventData.value
      ? eventGetters.getMetaDescription(eventData.value)
      : '',
    ...(eventData.value &&
      eventGetters.getOpenGraphImage(eventData.value) && {
        'og:image': eventGetters.getOpenGraphImage(eventData.value),
      }),
    'og:type': 'product',
    ...(eventData.value &&
      eventGetters.getMinimumPriceAmount(eventData.value) && {
        'product:price:amount': String(
          Number(eventGetters.getMinimumPriceAmount(eventData.value)) / 100
        ),
      }),
    'product:price:currency': eventData.value
      ? eventGetters.getMinimumPriceCurrency(eventData.value) || ''
      : '',
    'og:availability': 'instock',
  },
}))
useSeoHelper(seoOptions)
</script>

<style lang="postcss" scoped>
#sectionPartner {
  @apply max-w-[812px];
}
</style>
